import React from 'react';
import { View, Text, StyleSheet, TouchableOpacity, Platform } from 'react-native';
import { Image } from 'expo-image';
import { useNavigation } from '@react-navigation/native';
import { Feather } from '@expo/vector-icons';
import { spacing, typography } from '../../theme';
import { useColors, ThemeColors } from '../../hooks/useColors';
import type { Vendor } from '../../types/supabase';
import { Button } from '../ui/Button';
import type { NativeStackNavigationProp } from '@react-navigation/native-stack';
import type { RootStackParamList } from '../../types/navigation';

interface StorefrontCustomizationProps {
  vendor: Vendor;
}

export function CompactStorefrontCustomization({ vendor }: StorefrontCustomizationProps) {
  const colors = useColors();
  const styles = createStyles(colors);
  const navigation = useNavigation<NativeStackNavigationProp<RootStackParamList>>();

  return (
    <View style={styles.container}>
      {/* Banner Image */}
      <TouchableOpacity 
        style={styles.bannerContainer}
        activeOpacity={1}
      >
        {vendor.banner_image ? (
          <Image
            source={{ uri: vendor.banner_image }}
            style={styles.bannerImage}
            contentFit="cover"
            cachePolicy="memory-disk"
            transition={0}
          />
        ) : (
          <View style={styles.bannerPlaceholder}>
            <Feather name="image" size={20} color={colors.textLight} />
            <Text style={styles.placeholderText}>Add Banner Image</Text>
          </View>
        )}
      </TouchableOpacity>

      {/* Logo Image */}
      <TouchableOpacity 
        style={styles.logoContainer}
        activeOpacity={1}
      >
        {vendor.logo_image ? (
          <Image
            source={{ uri: vendor.logo_image }}
            style={styles.logoImage}
            contentFit="cover"
            cachePolicy="memory-disk"
            transition={0}
          />
        ) : (
          <View style={styles.logoPlaceholder}>
            <Feather name="camera" size={20} color={colors.textLight} />
          </View>
        )}
      </TouchableOpacity>

      {/* Address and Edit Button Row */}
      <View style={styles.bottomRow}>
        <View style={styles.addressCard}>
          <View style={styles.addressInfo}>
            <Text style={styles.addressText}>{vendor.street_line1}</Text>
            <Text style={styles.addressDetails}>
              {vendor.city}, {vendor.state} {vendor.zip_code}
            </Text>
          </View>
        </View>
        
        <Button
          onPress={() => navigation.navigate('VendorSettings')}
          variant="primary"
          style={styles.editButton}
          textStyle={styles.editButtonText}
          icon="edit-2"
          size="sm"
          iconSize={14}
        >
          Edit Store
        </Button>
      </View>
    </View>
  );
}

const createStyles = (colors: ThemeColors) => StyleSheet.create({
  container: {
    backgroundColor: colors.background,
    borderRadius: 12,
    overflow: 'hidden',
    marginTop: spacing.md,
    borderWidth: 1,
    borderColor: colors.border,
  },
  bannerContainer: {
    width: '100%',
    height: 120,
    backgroundColor: colors.surface,
  },
  bannerImage: {
    width: '100%',
    height: '100%',
  },
  bannerPlaceholder: {
    width: '100%',
    height: '100%',
    backgroundColor: colors.surface,
    alignItems: 'center',
    justifyContent: 'center',
    borderBottomWidth: 1,
    borderBottomColor: colors.border,
  },
  placeholderText: {
    fontSize: 13,
    fontWeight: '600',
    color: colors.textLight,
    marginTop: 8,
  },
  logoContainer: {
    position: 'absolute',
    left: spacing.md,
    top: 90,
    zIndex: 10,
  },
  logoImage: {
    width: 60,
    height: 60,
    borderRadius: 30,
    borderWidth: 3,
    borderColor: colors.background,
    backgroundColor: colors.surface,
  },
  logoPlaceholder: {
    width: 60,
    height: 60,
    borderRadius: 30,
    backgroundColor: colors.surface,
    alignItems: 'center',
    justifyContent: 'center',
    borderWidth: 3,
    borderColor: colors.background,
    shadowColor: "#000",
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 4,
    elevation: 3,
  },
  bottomRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingHorizontal: spacing.md,
    paddingTop: 40,
    paddingBottom: spacing.md,
  },
  addressCard: {
    flex: 1,
    marginRight: spacing.sm,
  },
  addressInfo: {
    flex: 1,
  },
  addressText: {
    fontSize: 14,
    color: colors.text,
    fontWeight: '700',
    marginBottom: 2,
  },
  addressDetails: {
    fontSize: 12,
    color: colors.textLight,
    fontWeight: '500',
  },
  editButton: {
    paddingHorizontal: spacing.md,
    height: 36,
    borderRadius: 18,
    minWidth: 110,
  },
  editButtonText: {
    fontSize: 13,
    fontWeight: '700',
  },
}); 
